import React, { useState } from 'react'
import { useSnapshot } from 'valtio'
import { openURL } from 'renderer/viewer/lib/simpleUtils'
import { miscUiState } from '../../globalState'
import { openNavAuthDropdown, utopiaAuthState } from './utopiaAuthState'
import UtopiaNav, { UtopiaPageId } from './UtopiaNav'
import UtopiaJoin from './UtopiaJoin'
import UtopiaStats from './UtopiaStats'
import UtopiaCguModal from './UtopiaCguModal'
import { openUtopiaLegalModal } from './utopiaOverlayState'
import UtopiaLegalModal from './UtopiaLegalModal'
import { getUtopiaPlayButtonState, utopiaServerStatusState } from './utopiaServerStatus'
import styles from './utopiaLanding.module.css'

const FEATURES = [
  { icon: '⚔️', label: 'Survie', desc: 'Économie, quêtes et progression' },
  { icon: '🏰', label: 'Créatif', desc: 'Construisez librement' },
  { icon: '🌍', label: 'Open World', desc: 'Monde persistant partagé' },
  { icon: '🤝', label: 'Communauté', desc: 'Jouez avec d\'autres joueurs' },
] as const

type Action = (e: React.MouseEvent<HTMLButtonElement>) => void

export type UtopiaLandingProps = {
  connectToServerAction?: Action
  optionsAction?: Action
  versionText?: string
  versionTitle?: string
  onVersionStatusClick?: () => void
  onVersionTextClick?: () => void
  bottomRightLinks?: string
}

export default function UtopiaLanding ({
  connectToServerAction,
  optionsAction,
  versionText,
  versionTitle,
  onVersionStatusClick,
  onVersionTextClick,
  bottomRightLinks,
}: UtopiaLandingProps) {
  const { appConfig } = useSnapshot(miscUiState)
  const { user } = useSnapshot(utopiaAuthState)
  useSnapshot(utopiaServerStatusState)
  const [page, setPage] = useState<UtopiaPageId>('home')
  const playBrowser = getUtopiaPlayButtonState('browser')

  const openAuth = () => openNavAuthDropdown()

  const handlePlay = () => {
    if (playBrowser.disabled) return
    if (!user) {
      openAuth()
      return
    }
    connectToServerAction?.(null as any)
  }

  let linksParsed = bottomRightLinks?.trim()
    ? bottomRightLinks.split(/;|\n/g).map(l => {
      const parts = l.split(':')
      return [parts[0], parts.slice(1).join(':')] as [string, string]
    })
    : undefined

  const description = appConfig?.rightSideText
    ?? 'Xbox, PlayStation, Switch, PC et navigateur web — un seul monde pour tous les joueurs.'

  return (
    <div className={styles.shell}>
      <img
        className={styles.backdropImage}
        src="./loading-bg.jpg"
        alt=""
        aria-hidden
      />
      <div className={styles.backdrop} aria-hidden />

      <UtopiaNav
        activePage={page}
        onPageChange={setPage}
        onOptionsClick={() => optionsAction?.(null as any)}
        onPlayClick={handlePlay}
      />

      <main className={styles.main}>
        {page === 'home' && (
          <div className={styles.homeContent}>
            <section className={styles.hero}>
              <h1 className={styles.infoTitle}>Bienvenue sur Utopie</h1>
              <p className={styles.heroPlatforms}>
                Minecraft <strong>Java</strong> &amp; <strong>Bedrock</strong>
                {' '}— disponible sur <strong>console, PC,</strong> et dans le <strong>navigateur</strong>
              </p>
              <p className={styles.infoDesc}>{description}</p>
              <div className={styles.playSection}>
                <button
                  type="button"
                  className={styles.playCta}
                  data-test-id="play-utopie-server-button"
                  onClick={handlePlay}
                  disabled={playBrowser.disabled}
                  title={playBrowser.disabled ? playBrowser.label : undefined}
                >
                  ▶ {playBrowser.label}
                </button>
                <p className={styles.playHint}>
                  {user ? (
                    <>
                      Connecté en tant que <strong>{user.gameUsername}</strong>
                    </>
                  ) : (
                    <>
                      Utilisez{' '}
                      <button
                        type="button"
                        className={styles.playHintLink}
                        onClick={openAuth}
                      >
                        Connexion
                      </button>
                      {' '}dans la barre du haut pour rejoindre le serveur
                    </>
                  )}
                </p>
              </div>
            </section>
            <div className={styles.features}>
              {FEATURES.map(f => (
                <div key={f.label} className={styles.featureCard}>
                  <span className={styles.featureIcon} aria-hidden>{f.icon}</span>
                  <div>
                    <div className={styles.featureLabel}>{f.label}</div>
                    <div className={styles.featureDesc}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {page === 'stats' && <UtopiaStats />}

        {page === 'join' && (
          <UtopiaJoin onPlayClick={handlePlay} onOpenAuth={openAuth} />
        )}
      </main>

      <footer className={styles.footer}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {versionText && (
            <span style={{ cursor: 'pointer' }} onClick={onVersionTextClick}>{versionText}</span>
          )}
          <span
            title={versionTitle}
            className={styles.productInfo}
            onClick={onVersionStatusClick}
          >
            Utopie Web Client {appConfig?.clientVersion ?? 'v1.1.0'}
          </span>
        </div>
        <div className={styles.footerRight}>
          <button
            type="button"
            className={styles.footerLegalBtn}
            onClick={openUtopiaLegalModal}
          >
            Mentions légales
          </button>
          {linksParsed && (
            <div className={styles.productLinks}>
              {linksParsed.map(([name, link], i, arr) => {
                if (!link.startsWith('http')) link = `https://${link}`
                const finalLink = link
                return (
                  <span key={name} style={{ color: 'lightgray', fontSize: 8 }}>
                    <a
                      style={{ cursor: 'pointer' }}
                      onClick={e => {
                        e.preventDefault()
                        openURL(finalLink, false)
                      }}
                    >
                      {name}
                    </a>
                    {i < arr.length - 1 && <span style={{ marginLeft: 2 }}>·</span>}
                  </span>
                )
              })}
            </div>
          )}
        </div>
      </footer>

      <div className={styles.shellOverlays}>
        <UtopiaCguModal />
        <UtopiaLegalModal />
      </div>
    </div>
  )
}
